import React, { useState, useEffect, useRef } from "react";
import { Box, Button, TextField, Typography, Paper } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useParams } from "react-router-dom";

function ChatInterface() {
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [connectionStatus, setConnectionStatus] = useState("Connecting...");
  const [chatEnded, setChatEnded] = useState(false);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const isConnectedRef = useRef(false);
  const [currentAssistantMessageId, setCurrentAssistantMessageId] =
    useState(null);
  const { jobId, resumeId } = useParams();
  const socketUrl = `wss://hr.aiassistant.co:8444/api/v1/${jobId}/${resumeId}/chat`;

  useEffect(() => {
    const connectWebSocket = () => {
      if (isConnectedRef.current) return;

      wsRef.current = new WebSocket(socketUrl);
      isConnectedRef.current = true;

      wsRef.current.onopen = () => {
        console.log("Connected to WebSocket");
        setConnectionStatus("Connected");
      };

      wsRef.current.onmessage = (event) => {
        const data = event.data;
        console.log("Received message:", data);

        if (data === "<EOM>") {
          setCurrentAssistantMessageId(null);
        } else if (data.includes("<EOS>")) {
          console.log("End of chat detected");
          const finalText = data.replace("<EOS>", "").trim();
          updateAssistantMessage(finalText);
          setTimeout(() => {
            setChatEnded(true);
          }, 2000);
        } else {
          updateAssistantMessage(data);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error("WebSocket Error:", error);
        setConnectionStatus("Error connecting");
      };

      wsRef.current.onclose = (event) => {
        console.log(`Connection closed: ${event.code} ${event.reason}`);
        setConnectionStatus("Disconnected");
        isConnectedRef.current = false;

        setTimeout(() => {
          setConnectionStatus("Connecting...");
          connectWebSocket();
        }, 2000);
      };
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        isConnectedRef.current = false;
      }
    };
  }, [socketUrl]);

  const addMessage = (text, type) => {
    setMessages((prevMessages) => [...prevMessages, { text, type }]);
  };

  const updateAssistantMessage = (data) => {
    if (currentAssistantMessageId === null) {
      setCurrentAssistantMessageId(messages.length);
      setMessages((prevMessages) => [
        ...prevMessages,
        { text: data, type: "assistant-message" },
      ]);
    } else {
      setMessages((prevMessages) =>
        prevMessages.map((message, index) =>
          index === currentAssistantMessageId
            ? { ...message, text: message.text + data }
            : message
        )
      );
    }
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const handleUserInput = (event) => {
    setUserInput(event.target.value);
  };

  const handleSendMessage = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const message = userInput;
      wsRef.current.send(message);
      addMessage(message, "user-message");
      setUserInput("");
    } else {
      console.error("WebSocket is not open.");
    }
  };

  const renderMessages = () => {
    return messages.map((message, index) => (
      <Paper
        key={index}
        className={`message ${message.type}`}
        elevation={3}
        style={{
          padding: "10px",
          margin: "10px 0",
          maxWidth: "600px",
          alignSelf:
            message.type === "user-message" ? "flex-end" : "flex-start",
          backgroundColor:
            message.type === "user-message" ? "#e3f2fd" : "#f1f8e9",
        }}
      >
        <Typography>{message.text}</Typography>
      </Paper>
    ));
  };

  if (chatEnded) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          justifyContent: "center",
          alignItems: "center",
          padding: "20px",
          backgroundColor: "#f9f9f9",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: "30px",
            maxWidth: "600px",
            width: "100%",
            textAlign: "center",
            backgroundColor: "#f8f8f8",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              mb: 2,
              fontFamily: "Montserrat",
              fontWeight: 700,
              color: "#202224",
            }}
          >
            Chat Interview is Over
          </Typography>
          <Typography variant="h6" sx={{ color: "#555" }}>
            We will get back to you shortly.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        justifyContent: "center",
        alignItems: "center",
        padding: "20px",
        backgroundColor: "#f9f9f9",
      }}
    >
      <Typography
        variant="h4"
        gutterBottom
        style={{
          fontFamily: "Montserrat",
          fontWeight: 700,
          textAlign: "left",
          color: "#202224",
          opacity: 0.8,
        }}
      >
        Chat Interface
      </Typography>
      <Typography variant="h6" gutterBottom>
        {connectionStatus}
      </Typography>
      <Box
        sx={{
          width: "100%",
          maxWidth: 1000,
          display: "flex",
          flexDirection: "column",
          height: "70vh",
          border: "2px solid #ccc",
          borderRadius: "12px",
          overflowY: "auto",
          padding: "10px",
          backgroundColor: "#fafafa",
        }}
      >
        {renderMessages()}
        <div ref={messagesEndRef} />
      </Box>
      <Box sx={{ width: "100%", maxWidth: 1000, display: "flex", mt: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          value={userInput}
          onChange={handleUserInput}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault(); // Prevent newline in TextField
              handleSendMessage();
            }
          }}
          placeholder="Type your message..."
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSendMessage}
          sx={{ ml: 2 }}
          endIcon={<SendIcon />}
        >
          Send
        </Button>
      </Box>
    </Box>
  );
}

export default ChatInterface;
